import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { Trans } from "@lingui/react/macro";

import LandingLayout from "../../components/LandingLayout";
import "../../components/blog/blog-prose.css";
import { getPostBySlug } from "../../lib/blog";

const SITE_URL = "https://www.bodaweb.site";
const DEFAULT_OG_IMAGE = "/hero.jpg";

export const Route = createFileRoute("/blog/$slug")({
  component: BlogPostPage,
  // The head function runs at SSR time with the resolved route params, so
  // we can do a synchronous lookup against the build-time-bundled posts and
  // emit per-post OpenGraph + Twitter meta. WhatsApp/Slack/Twitter previews
  // depend on this being rendered into the HTML response.
  head: ({ params }) => {
    const post = getPostBySlug(params.slug);
    if (!post) {
      return { meta: [{ title: "BodaWeb — Blog" }] };
    }
    const url = `${SITE_URL}/blog/${post.slug}`;
    const imagePath = post.image ?? DEFAULT_OG_IMAGE;
    const image = imagePath.startsWith("http")
      ? imagePath
      : `${SITE_URL}${imagePath}`;
    const fullTitle = `${post.title} — BodaWeb`;
    return {
      meta: [
        { title: fullTitle },
        { content: post.description, name: "description" },
        // OpenGraph (Facebook, WhatsApp, LinkedIn, etc.)
        { content: post.title, property: "og:title" },
        { content: post.description, property: "og:description" },
        { content: "article", property: "og:type" },
        { content: url, property: "og:url" },
        { content: image, property: "og:image" },
        { content: "BodaWeb", property: "og:site_name" },
        { content: "es_ES", property: "og:locale" },
        { content: post.date, property: "article:published_time" },
        // Twitter Cards (summary_large_image because /hero.jpg is wide)
        { content: "summary_large_image", name: "twitter:card" },
        { content: post.title, name: "twitter:title" },
        { content: post.description, name: "twitter:description" },
        { content: image, name: "twitter:image" },
      ],
    };
  },
});

const dateFormatter = new Intl.DateTimeFormat("es-ES", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

function formatPostDate(iso: string): string {
  const [year, month, day] = iso
    .split("-")
    .map((part) => Number.parseInt(part, 10));
  if (!year || !month || !day) {
    return iso;
  }
  return dateFormatter.format(new Date(year, month - 1, day));
}

function BlogPostPage() {
  const { slug } = Route.useParams();
  const post = getPostBySlug(slug);

  // The route is statically rendered with whatever slug shows up; if the slug
  // doesn't match a known post we surface a 404 so search engines and the user
  // get the right signal.
  if (!post) {
    throw notFound();
  }

  const PostBody = post.Component;

  return (
    <LandingLayout showFooter>
      <main className="overflow-x-hidden">
        <article className="mx-auto max-w-3xl px-4 pb-20 pt-28 sm:px-6 sm:pb-24 sm:pt-32">
          <Link
            className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--color-muted)] transition-colors hover:text-[var(--color-accent)]"
            data-testid="blog-post-back-to-index"
            to="/blog"
          >
            <Trans>← Volver al blog</Trans>
          </Link>

          <header className="mt-8 border-b border-[var(--color-border)] pb-10">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--color-muted)]">
              {formatPostDate(post.date)}
            </p>
            <h1 className="mt-4 font-display text-5xl italic leading-[0.95] tracking-[-0.02em] text-[var(--color-ink)] sm:text-6xl">
              {post.title}
            </h1>
            {post.description ? (
              <p className="mt-5 max-w-2xl text-base leading-7 text-[var(--color-muted)] sm:text-lg">
                {post.description}
              </p>
            ) : null}
          </header>

          <div className="blog-prose mt-10" data-testid="blog-post-body">
            <PostBody />
          </div>
        </article>
      </main>
    </LandingLayout>
  );
}
